import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Search, PlusCircle, Filter, Briefcase, MapPin, DollarSign } from "lucide-react";
import { API_BASE_URL } from "../config/apiconfig";
import { ui } from "../config/theme";

const CLASSIFICATIONS = [
    "Accounting", "Administration & Office Support", "Advertising, Arts & Media", 
    "Banking & Financial Services", "Call Centre & Customer Service", 
    "CEO & General Management", "Community Services & Development", "Construction", 
    "Consulting & Strategy", "Design & Architecture", "Education & Training", 
    "Engineering", "Farming, Animals & Conservation", "Government & Defence", 
    "Healthcare & Medical", "Hospitality & Tourism", "Human Resources & Recruitment", 
    "Information & Communication Technology", "Insurance & Superannuation", 
    "Legal", "Manufacturing, Transport & Logistics", "Marketing & Communications", 
    "Mining, Resources & Energy", "Real Estate & Property", "Retail & Consumer Products", 
    "Sales", "Science & Technology", "Self Employment", "Sport & Recreation", 
    "Trades & Services"
];

const JobSearch = () => {
    const [jobs, setJobs] = useState([]);
    const [filteredJobs, setFilteredJobs] = useState([]);
    const [searchParams, setSearchParams] = useState({
        keywords: "",
        classification: "Any Classification",
        location: "",
        workType: "All work types",
        payRange: "paying $0",
        salaryRange: "to $350K+",
        listingTime: "listed any time"
    });
    const [isAdvancedFilterOpen, setIsAdvancedFilterOpen] = useState(false);
    const navigate = useNavigate();
    const { user } = useAuth();

    // User region based filtering
    const [userCountry, setUserCountry] = useState("Sri Lanka");
    const isSriLankaUser = useMemo(() => userCountry === 'Sri Lanka', [userCountry]);
    const [lkLocations, setLkLocations] = useState([]);
    const [provinceFilter, setProvinceFilter] = useState("");
    const [districtFilter, setDistrictFilter] = useState("");
    const provinceOptions = useMemo(() => (lkLocations || []).map(p => p.province), [lkLocations]);
    const districtOptions = useMemo(() => {
        const p = (lkLocations || []).find(x => x.province === provinceFilter);
        return p ? (p.districts || []).map(d => d.district) : [];
    }, [lkLocations, provinceFilter]);

    // Note: avoid profile-by-email fetch here to prevent noisy 404s in some deployments.
    // Job search defaults to Sri Lanka unless user explicitly changes filters.

    useEffect(() => {
        if (isSriLankaUser && lkLocations.length === 0) {
            (async () => {
                try {
                    const r = await fetch('https://general.apivcm.shop/sri-lanka-locations', { cache: 'no-store' });
                    if (r.ok) {
                        const j = await r.json();
                        setLkLocations(Array.isArray(j?.data) ? j.data : []);
                    }
                } catch {}
            })();
        }
    }, [isSriLankaUser, lkLocations.length]);

    // Fetch jobs from backend with region filters
    useEffect(() => {
        const fetchJobs = async () => {
            try {
                const params = new URLSearchParams();
                params.append('country', userCountry || 'Sri Lanka');
                if (isSriLankaUser && provinceFilter) params.append('province', provinceFilter);
                if (isSriLankaUser && districtFilter) params.append('district', districtFilter);
                params.append('status', 'Active');
                if (userCountry || provinceFilter || districtFilter) params.append('partial', '1');
                const response = await fetch(`${API_BASE_URL}/api/job-postings?${params.toString()}`);
                const data = await response.json();
                setJobs(data);
            } catch (error) {
                console.error("Error fetching jobs:", error);
            }
        };
        fetchJobs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userCountry, provinceFilter, districtFilter]);

    useEffect(() => {
        const filterJobs = () => {
            return jobs.filter(job => {
                const matchKeywords = !searchParams.keywords || 
                    job.title.toLowerCase().includes(searchParams.keywords.toLowerCase()) ||
                    job.location.toLowerCase().includes(searchParams.keywords.toLowerCase());
                
                const matchClassification = searchParams.classification === "Any Classification" || 
                    job.classification === searchParams.classification;
                
                const matchLocation = !searchParams.location || 
                    (job.location || '').toLowerCase().includes(searchParams.location.toLowerCase());

                // Client-side region filters (in addition to server filtering)
                const lc = (job.location_country || '').toLowerCase().trim();
                const pv = (job.province || '').toLowerCase().trim();
                const dt = (job.district || '').toLowerCase().trim();
                const legacy = (job.location || '').toLowerCase();

                const uc = (userCountry || '').toLowerCase().trim();
                const matchCountry = !uc || lc === uc || (!lc && legacy.includes(uc));
                const matchProvince = !isSriLankaUser || !provinceFilter || pv === provinceFilter.toLowerCase().trim() || (!pv && legacy.includes(provinceFilter.toLowerCase()));
                const matchDistrict = !isSriLankaUser || !districtFilter || dt === districtFilter.toLowerCase().trim() || (!dt && legacy.includes(districtFilter.toLowerCase()));

                return matchKeywords && matchClassification && matchLocation && matchCountry && matchProvince && matchDistrict;
            });
        };

        setFilteredJobs(filterJobs());
    }, [searchParams, jobs, userCountry, isSriLankaUser, provinceFilter, districtFilter]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setSearchParams(prev => ({
            ...prev,
            [name]: value
        }));
    };

    return (
        <div className={ui.page}>
            <Header />
            <main className="flex-grow">
                <div className={ui.container}>
                    {/* Header Section */}
                    <div className="w-full mb-8">
                        <h1 className={ui.heading}>Find Your Dream Job</h1>
                        {user?.role === 'e2e-admin' && (
                            <button
                                onClick={() => navigate("/create-job")}
                                className={`${ui.buttonPrimarySm} mt-4`}
                            >
                                <PlusCircle className="w-5 h-5" />
                                <span>Add Job Posting</span>
                            </button>
                        )}
                    </div>

                    {/* Search and Filters */}
                    <div className={`${ui.sectionCard} mb-8`}>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            {/* Keywords */}
                            <div className="relative">
                                <input
                                    type="text"
                                    name="keywords"
                                    placeholder="Keywords (e.g., Software Engineer)"
                                    value={searchParams.keywords}
                                    onChange={handleInputChange}
                                    className={ui.input}
                                />
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                            </div>

                            {/* Classification */}
                            <select
                                name="classification"
                                value={searchParams.classification}
                                onChange={handleInputChange}
                                className={ui.select}
                            >
                                <option value="Any Classification">Any Classification</option>
                                {CLASSIFICATIONS.map(cls => (
                                    <option key={cls} value={cls}>{cls}</option>
                                ))}
                            </select>

                            {/* Location */}
                            <div className="relative">
                                <input
                                    type="text"
                                    name="location"
                                    placeholder="Suburb, City, or Region"
                                    value={searchParams.location}
                                    onChange={handleInputChange}
                                    className={ui.input}
                                />
                                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                            </div>

                            {/* Sri Lanka regional filters */}
                            {isSriLankaUser && (
                                <>
                                <select
                                    value={provinceFilter}
                                    onChange={e => { setProvinceFilter(e.target.value); setDistrictFilter(""); }}
                                    className={ui.select}
                                >
                                    <option value="">All Provinces</option>
                                    {provinceOptions.map(p => (<option key={p} value={p}>{p}</option>))}
                                </select>
                                <select
                                    value={districtFilter}
                                    onChange={e => setDistrictFilter(e.target.value)}
                                    className={ui.select}
                                    disabled={!provinceFilter}
                                >
                                    <option value="">All Districts</option>
                                    {districtOptions.map(d => (<option key={d} value={d}>{d}</option>))}
                                </select>
                                </>
                            )}

                            {/* Search Button */}
                            <button 
                                className={`${ui.buttonPrimary} w-full sm:w-auto font-medium`}
                            >
                                Search Jobs
                            </button>
                        </div>

                        {/* Advanced Filters */}
                        <div className="mt-4">
                            <button 
                                onClick={() => setIsAdvancedFilterOpen(!isAdvancedFilterOpen)}
                                className={ui.buttonLink}
                            >
                                <Filter className="w-4 h-4 mr-2" />
                                {isAdvancedFilterOpen ? 'Hide Advanced Filters' : 'Show Advanced Filters'}
                            </button>
                            {isAdvancedFilterOpen && (
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                                    <select
                                        name="workType"
                                        value={searchParams.workType}
                                        onChange={handleInputChange}
                                        className={ui.select}
                                    >
                                        <option>All work types</option>
                                        <option>Full-time</option>
                                        <option>Part-time</option>
                                        <option>Contract</option>
                                        <option>Casual</option>
                                    </select>
                                    <select
                                        name="payRange"
                                        value={searchParams.payRange}
                                        onChange={handleInputChange}
                                        className={ui.select}
                                    >
                                        <option>paying $0</option>
                                        <option>paying $1-$50K</option>
                                        <option>paying $50K-$100K</option>
                                        <option>paying $100K-$150K</option>
                                    </select>
                                    <select
                                        name="salaryRange"
                                        value={searchParams.salaryRange}
                                        onChange={handleInputChange}
                                        className={ui.select}
                                    >
                                        <option>to $350K+</option>
                                        <option>to $50K</option>
                                        <option>to $100K</option>
                                        <option>to $200K</option>
                                    </select>
                                    <select
                                        name="listingTime"
                                        value={searchParams.listingTime}
                                        onChange={handleInputChange}
                                        className={ui.select}
                                    >
                                        <option>listed any time</option>
                                        <option>listed today</option>
                                        <option>listed this week</option>
                                        <option>listed this month</option>
                                    </select>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Job Listings */}
                    <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredJobs.length > 0 ? (
                            filteredJobs.map((job) => (
                                <div
                                    key={job.id}
                                    className={`${ui.sectionCard} hover:shadow-xl transition-all duration-300 flex flex-col`}
                                >
                                    <div className="flex-grow">
                                        <h2 className="text-xl font-semibold text-slate-800 mb-3 truncate">{job.title}</h2>
                                        <div className="space-y-2 mb-4">
                                            <p className="text-slate-600 text-sm flex items-center">
                                                <Briefcase className="w-4 h-4 mr-2 text-blue-500" />
                                                <span className="font-medium">Status:</span> {job.status}
                                            </p>
                                            <p className="text-slate-600 text-sm flex items-center">
                                                <DollarSign className="w-4 h-4 mr-2 text-blue-500" />
                                                <span className="font-medium">Salary:</span> ${job.salary}
                                            </p>
                                            <p className="text-slate-600 text-sm flex items-center">
                                                <MapPin className="w-4 h-4 mr-2 text-blue-500" />
                                                <span className="font-medium">Location:</span> {job.location}
                                            </p>
                                        </div>
                                    </div>
                                    {job.company_name && (
                                        <div className="mt-2 pt-2 border-t border-slate-200 text-sm text-slate-600">
                                            Company: <span className="font-medium">{job.company_name}</span>
                                        </div>
                                    )}
                                    <Link
                                        to={`/job-details/${job.id}`}
                                        className={`${ui.buttonLink} mt-4`}
                                    >
                                        View Details
                                        <span className="ml-1">→</span>
                                    </Link>
                                </div>
                            ))
                        ) : (
                            <div className="w-full col-span-full text-center py-8">
                                <p className="text-slate-600 text-lg font-medium">No jobs found matching your criteria.</p>
                                <p className="text-slate-500 text-sm mt-2">Try adjusting your search filters to find more opportunities.</p>
                            </div>
                        )}
                    </div>
                </div>
            </main>
            <Footer />
        </div>
    );
};

export default JobSearch;